import _ from 'lodash';
import {connect} from 'react-redux';

import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {isTeamInbox} from '../../../../../helpers/modelHelpers';
import {buildPublicId} from '../../../../../models/applications/publicIdTypes';
import {InboxesSearchLocationsEnum} from '../../../../../models/url/models/inboxesUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../services/tracking';
import {AppState} from '../../../../../store/appState';
import {ThunkAction, ThunkAsyncAction} from '../../../../../store/appThunk';
import {fetchDuplicateConversationsAsyncAction} from '../../../../../store/data/conversations/conversationsActions';
import {getConversations} from '../../../../../store/data/dataSelectors';
import {getInboxes} from '../../../../../store/data/inboxes/inboxesSelectors';
import {InboxesSearchParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {normalInboxesSelection} from '../../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {mergeConversationsAndNavigateAsyncAction} from '../../../../../store/panels/viewer/viewerPanelConversationsActions';
import {searchByParametersAction} from '../../../../../store/stateless/searchActions';
import {DuplicateConversationBanner} from './duplicateConversationBanner';

/*
 * Tracking event actions.
 */

enum DuplicateConversationBannerActionsEnum {
  BANNER_DISPLAYED = 'banner_displayed',
  VIEW_DUPLICATES = 'view_duplicates',
  MERGE_DUPLICATES = 'merge_duplicates',
  LEARN_MORE = 'learn_more',
}

interface DuplicateConversationBannerContainerProps {
  conversationId: number;
  duplicateConversationIds: ReadonlyArray<number>;
  shouldBannerHaveBeenHidden: boolean;
}

/*
 * Selectors.
 */

function getConversationId(_state: AppState, props: DuplicateConversationBannerContainerProps) {
  return props.conversationId;
}

function getDuplicateConversationIds(_state: AppState, props: DuplicateConversationBannerContainerProps) {
  return props.duplicateConversationIds;
}

function makeGetDuplicateConversations() {
  return createSelector(
    [getConversationId, getDuplicateConversationIds, getConversations],
    (conversationId, duplicateConversationIds, conversations) =>
      _([conversationId, ...duplicateConversationIds])
        .map((duplicateConversationId) => conversations[duplicateConversationId])
        .compact()
        .value(),
  );
}

/* Return all team inboxes which contain at least one duplicate conversation. */
function makeGetDuplicateConversationTeamInboxes(
  getDuplicateConversations: ReturnType<typeof makeGetDuplicateConversations>,
) {
  return createSelector([getDuplicateConversations, getInboxes], (duplicateConversations, inboxes) =>
    _(duplicateConversations)
      .map((duplicateConversation) => duplicateConversation.inboxIds)
      .flatten()
      .uniq()
      .map((inboxId) => inboxes[inboxId])
      .compact()
      .filter(isTeamInbox)
      .value(),
  );
}

function makeGetDuplicateConversationMessageCount(
  getDuplicateConversations: ReturnType<typeof makeGetDuplicateConversations>,
) {
  return createSelector(
    [getDuplicateConversations],
    (duplicateConversations) => _(duplicateConversations).first()?.numMessages || 0,
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<DuplicateConversationBannerContainerProps>()(() => {
  const getDuplicateConversations = makeGetDuplicateConversations();

  return {
    duplicateConversationTeamInboxes: makeGetDuplicateConversationTeamInboxes(getDuplicateConversations),
    duplicateConversationMessageCount: makeGetDuplicateConversationMessageCount(getDuplicateConversations),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<DuplicateConversationBannerContainerProps>()(
  ({shouldBannerHaveBeenHidden}) => ({
    onBannerDisplayed:
      (conversationId: number): ThunkAction =>
      () => {
        trackInterfaceEvent(InterfaceEventsEnum.DUPLICATE_CONVERSATION_BANNER_ACTION, {
          action: DuplicateConversationBannerActionsEnum.BANNER_DISPLAYED,
          conversation_id: conversationId,
          should_banner_have_been_hidden: shouldBannerHaveBeenHidden,
        });
      },
    onFetchConversationDuplicates:
      (conversationId: number): ThunkAction =>
      (dispatch) => {
        dispatch(fetchDuplicateConversationsAsyncAction(conversationId));
      },
    onSearchConversationDuplicatesClick:
      (conversationId: number): ThunkAction =>
      (dispatch, getState) => {
        trackInterfaceEvent(InterfaceEventsEnum.DUPLICATE_CONVERSATION_BANNER_ACTION, {
          action: DuplicateConversationBannerActionsEnum.VIEW_DUPLICATES,
          conversation_id: conversationId,
        });

        const conversationPublicId = buildPublicId('conversation', conversationId);
        const query = `copies:${conversationPublicId}`;

        const searchParameters: InboxesSearchParameters = {
          location: InboxesSearchLocationsEnum.GLOBAL,
          query,
          fromSelection: normalInboxesSelection([conversationId]),
        };

        dispatch(searchByParametersAction(searchParameters));
      },
    onMergeConversationsClick:
      (conversationIds: ReadonlyArray<number>): ThunkAsyncAction =>
      async (dispatch) => {
        trackInterfaceEvent(InterfaceEventsEnum.DUPLICATE_CONVERSATION_BANNER_ACTION, {
          action: DuplicateConversationBannerActionsEnum.MERGE_DUPLICATES,
          /** First conversationId belongs to the banner. */
          conversation_id: conversationIds[0],
          should_banner_have_been_hidden: shouldBannerHaveBeenHidden,
        });

        await dispatch(mergeConversationsAndNavigateAsyncAction(conversationIds));
      },
    onLearnMoreClick:
      (conversationId: number): ThunkAction =>
      () => {
        trackInterfaceEvent(InterfaceEventsEnum.DUPLICATE_CONVERSATION_BANNER_ACTION, {
          action: DuplicateConversationBannerActionsEnum.LEARN_MORE,
          conversation_id: conversationId,
        });

        openInBrowser(
          'https://help.frontapp.com/t/m2bx52/smart-merge-reduces-duplicate-emails#merge-duplicates-with-one-click',
        );
      },
  }),
);

export const DuplicateConversationBannerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(DuplicateConversationBanner);
